import { useState } from 'react';
import { useShallow } from 'zustand/shallow';

import { isServiceDisabledForTimeWindowSelection } from '@/feature/time-window-update.feature';
import { useAppStore } from '@/store/app.store';
import { useTimeWindowUpdateStore } from '@/store/time-window-update.store';
import {
    selectAssetStatus,
    selectBuCode,
    selectOrderType,
} from '@repo/api-adapters/selectors/customer-assets.selectors';
import type { SelectedServices } from '@repo/api-adapters/selectors/services.selectors';

import { getServiceBaseUrl } from '@/api/helpers.api.ts';
import { useAssetServices } from '@/hooks/asset-services.hooks';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { getArticleNo } from '@/utils/article.utils';
import {
    formatServiceSummaryDetails,
    formatServiceTimeWindow,
    getServiceUniqueKey,
    isFullServeHandoutDelivery,
} from '@/utils/services.utils';
import { formatPrice } from '@/utils/translation.utils';
import { getAppConfig } from '@/utils/window.utils';

import { useIsTimeWindowSelectionEnabled } from '@/hooks/time-window-update.hooks';
import type { FulfillmentService } from './fulfillment.types';

export const useFulfillmentServices = () => {
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const services = useAssetServices(data);
    const isAssetDetailsEditMode = useAppStore((state) => state.assetDetails.mode === 'edit');
    const [selectedService, setSelectedService] = useState<SelectedServices[number]>();
    const { setSelectedReasonCode, setHasSubmitError } = useTimeWindowUpdateStore(
        useShallow((state) => ({
            setSelectedReasonCode: state.setSelectedReasonCode,
            setHasSubmitError: state.setHasSubmitError,
        }))
    );
    const orderType = selectOrderType(data);
    const assetStatus = selectAssetStatus(data);
    const config = getAppConfig();
    const shouldShowPriceExcludingTax = config.feature.priceExclTax?.enable;
    const hasDeliveryServices = services.some((service) => service.isDelivery === true);
    const hasProvidedServices = services.some((service) => service.isDelivery !== true);

    const assetBuCode = selectBuCode(data) ?? '';
    const coworkerBaseUrl = getServiceBaseUrl(config.api.services.orderTaking, 'coworker');
    const isDeliveryTimeWindowSelectionEnabled = useIsTimeWindowSelectionEnabled(
        'delivery',
        { assetType, orderType, assetStatus, hasDeliveryServices, hasProvidedServices },
        coworkerBaseUrl,
        assetBuCode
    );
    const isProvidedServiceTimeWindowSelectionEnabled = useIsTimeWindowSelectionEnabled(
        'providedService',
        { assetType, orderType, assetStatus, hasDeliveryServices, hasProvidedServices },
        coworkerBaseUrl,
        assetBuCode
    );

    const fulfillmentServices: FulfillmentService[] = services.map((service, index) => {
        const id = getServiceUniqueKey(service) ?? `${service.serviceName ?? 'service'}-${index}`;
        const price = formatPrice(
            shouldShowPriceExcludingTax ? service.price?.priceExclTax : service.price?.priceInclTax,
            service.price?.currencyCode
        );
        const timeWindow = service.timeWindow ? formatServiceTimeWindow(service.isDelivery, service.timeWindow) : '';
        const summaryDetails = formatServiceSummaryDetails(
            service.serviceProviderName,
            service.timeWindow?.fulfillmentUnit
        );
        const timeWindowCategory = service.isDelivery === true ? 'delivery' : 'providedService';
        const canEditTimeWindow =
            !isServiceDisabledForTimeWindowSelection(service.serviceId) &&
            (timeWindowCategory === 'delivery'
                ? isDeliveryTimeWindowSelectionEnabled
                : isProvidedServiceTimeWindowSelectionEnabled);
        const canOpenTimeWindowEditor = isAssetDetailsEditMode && canEditTimeWindow;

        return {
            id,
            articles: (service.referenceItems ?? []).map((article, articleIndex) => ({
                id: article.itemId ?? `${id}-article-${articleIndex}`,
                ...(article.description && { description: article.description }),
                name: article.name ?? article.retailItemName ?? article.retailItemDescription ?? '',
                quantity: article.quantity?.toFixed(0) ?? '',
                ...(article.itemId && { articleNumber: getArticleNo(article.itemId) }),
                ...(article.isReferenceItem && { isReferenceItem: article.isReferenceItem }),
            })),
            isDelivery: service.isDelivery === true,
            isFullServeHandoutDelivery: isFullServeHandoutDelivery(service),
            serviceName: service.serviceName ?? '',
            ...(summaryDetails && { summaryDetails }),
            ...(price && { price }),
            ...(timeWindow && {
                timeWindow: {
                    value: timeWindow,
                    ...(canOpenTimeWindowEditor && {
                        onClick: () => {
                            setSelectedService(service);
                        },
                    }),
                },
            }),
        };
    });

    const closeTimeWindowModal = () => {
        setSelectedService(undefined);
        setSelectedReasonCode('');
        setHasSubmitError(false);
    };

    return {
        closeTimeWindowModal,
        fulfillmentServices,
        selectedService,
    };
};
